import React, { useEffect, useMemo, useState } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Link, useLocation } from 'react-router-dom';
import PaymentForm from '../components/payments/PaymentForm';
import DemoPaymentForm from '../components/payments/DemoPaymentForm';
import CheckoutSummary from '../components/payments/CheckoutSummary';
import PaymentHistory from '../components/payments/PaymentHistory';
import { getSystemHealth, type SystemHealthSnapshot } from '../services/admin';
import useAuth from '../hooks/useAuth';

const stripePublicKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY?.trim();

const isUsableStripePublishableKey = (value: string | undefined): value is string => {
  if (!value) {
    return false;
  }

  if (value.includes('your_stripe_publishable_key_here')) {
    return false;
  }

  return value.startsWith('pk_test_') || value.startsWith('pk_live_');
};

const stripePromise = isUsableStripePublishableKey(stripePublicKey)
  ? loadStripe(stripePublicKey)
  : null;

const Payment: React.FC = () => {
  const location = useLocation();
  const [quotedAmount, setQuotedAmount] = useState<number | null>(null);
  const [systemHealth, setSystemHealth] = useState<SystemHealthSnapshot | null>(null);
  const [healthLoading, setHealthLoading] = useState(true);
  const { user } = useAuth();

  const wizardState = location.state as
    | {
        fromCampaignWizard?: boolean;
        budget?: number;
        packageId?: string | null;
        packageName?: string | null;
        adId?: string | null;
      }
    | undefined;

  const initialAmountFromWizard = useMemo(() => {
    if (!wizardState || !wizardState.fromCampaignWizard) return null;
    if (typeof wizardState.budget !== 'number' || wizardState.budget <= 0) return null;
    return wizardState.budget;
  }, [wizardState]);

  const isAdmin = (() => {
    if (!user) return false;
    const maybeNested = user as Record<string, unknown>;
    const inner =
      maybeNested.user && typeof maybeNested.user === 'object'
        ? (maybeNested.user as Record<string, unknown>)
        : null;

    const roleValue =
      (inner && typeof inner.role === 'string' && (inner.role as string)) ||
      (typeof maybeNested.role === 'string' && (maybeNested.role as string)) ||
      undefined;

    return roleValue === 'admin';
  })();

  const stripeConfigMessage = (() => {
    if (!stripePublicKey) {
      return 'Set REACT_APP_STRIPE_PUBLIC_KEY to your Stripe publishable key and restart the frontend.';
    }

    if (!isUsableStripePublishableKey(stripePublicKey)) {
      return 'REACT_APP_STRIPE_PUBLIC_KEY is still a placeholder or malformed value. Use a real Stripe publishable key that starts with pk_test_ or pk_live_.';
    }

    return 'Stripe is not configured correctly in this environment.';
  })();

  useEffect(() => {
    let cancelled = false;

    const loadHealth = async () => {
      setHealthLoading(true);
      try {
        const snapshot = await getSystemHealth();
        if (!cancelled) {
          setSystemHealth(snapshot);
        }
      } catch {
        if (!cancelled) {
          setSystemHealth(null);
        }
      } finally {
        if (!cancelled) {
          setHealthLoading(false);
        }
      }
    };

    void loadHealth();

    return () => {
      cancelled = true;
    };
  }, []);

  const backendStripeConfigured = systemHealth?.stripe.configured === true;
  const canUseLiveStripe = !!stripePromise && backendStripeConfigured;

  const checkoutModeLabel = canUseLiveStripe ? 'Stripe checkout ready' : 'Demo checkout mode';

  const checkoutModeMessage = (() => {
    if (canUseLiveStripe) {
      return 'Frontend and backend Stripe configuration are both present. Card payments will use Stripe.';
    }

    if (healthLoading) {
      return 'Checking backend payment readiness…';
    }

    if (!stripePromise) {
      return stripeConfigMessage;
    }

    if (!systemHealth) {
      return 'The backend health check is unavailable, so checkout stays in demo mode for safety.';
    }

    if (!backendStripeConfigured) {
      return 'The backend is missing Stripe server credentials or webhook configuration, so checkout stays in demo mode.';
    }

    return 'Stripe is not configured correctly in this environment.';
  })();

  return (
    <main className="bg-slate-50 dark:bg-slate-900">
      <div className="mx-auto max-w-5xl px-4 py-6 space-y-6 sm:py-8">
        <header className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100 sm:text-3xl">Payments</h1>
            <p className="max-w-2xl text-sm text-slate-600 dark:text-slate-400">
              Securely fund and track your TELLURVISION campaigns in one place.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <Link
                to="/admin"
                className="inline-flex items-center rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-300 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Admin overview
              </Link>
            )}
            <Link
              to="/"
              className="inline-flex items-center rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-300 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Go home
            </Link>
          </div>
        </header>

        <section className="grid gap-4 md:grid-cols-3 md:items-start">
          <div className="md:col-span-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">Checkout status</p>
                <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">{checkoutModeMessage}</p>
              </div>
              <span
                className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${
                  canUseLiveStripe
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-amber-100 text-amber-700'
                }`}
              >
                {checkoutModeLabel}
              </span>
            </div>
          </div>
          {canUseLiveStripe ? (
            <Elements stripe={stripePromise}>
              <div className="md:col-span-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 shadow-sm">
                <PaymentForm
                  onAmountChange={setQuotedAmount}
                  initialAmount={initialAmountFromWizard}
                  campaignContext={
                    wizardState && wizardState.fromCampaignWizard
                      ? {
                          packageId: wizardState.packageId ?? null,
                          packageName: wizardState.packageName ?? null,
                          adId: wizardState.adId ?? null,
                        }
                      : null
                  }
                />
              </div>
            </Elements>
          ) : (
            <div className="md:col-span-2 space-y-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 shadow-sm">
              <div>
                <p className="font-medium">Using demo payment flow in this environment.</p>
                <p className="mt-1 text-xs">{checkoutModeMessage}</p>
              </div>
              <div className="rounded-lg border border-amber-200 bg-white dark:bg-slate-900 p-4 text-slate-900 dark:text-slate-100">
                <DemoPaymentForm
                  onAmountChange={setQuotedAmount}
                  initialAmount={initialAmountFromWizard}
                  campaignContext={
                    wizardState && wizardState.fromCampaignWizard
                      ? {
                          packageId: wizardState.packageId ?? null,
                          packageName: wizardState.packageName ?? null,
                          adId: wizardState.adId ?? null,
                        }
                      : null
                  }
                />
              </div>
            </div>
          )}
          <div className="space-y-4">
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 shadow-sm">
              <CheckoutSummary totalAmount={quotedAmount ?? 0} />
            </div>
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 shadow-sm">
              <PaymentHistory />
            </div>
          </div>
        </section>
      </div>
    </main>
  );
};

export default Payment;
